/**
 * Hi-Res Agro-Climatic Crop Selection panel.
 *
 * Renders the response of POST /api/advisory/crop-selection/hires as a
 * ranked table (crop-wise suitability) plus a site-parameters card. Extracted
 * from Suitability.jsx to keep that file focused on orchestration.
 */
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { AiAdvisor } from './common';

// Colour helper for the "Highly / Moderately / Marginally / Not Suitable" pill.
const scoreColor = (category) => {
  switch (category) {
    case 'Highly Suitable':    return 'bg-emerald-600 hover:bg-emerald-700';
    case 'Moderately Suitable':return 'bg-lime-500 hover:bg-lime-600';
    case 'Marginally Suitable':return 'bg-amber-500 hover:bg-amber-600';
    default:                   return 'bg-rose-500 hover:bg-rose-600';
  }
};

const PARAM_ORDER = ['rainfall', 'temp_max', 'temp_min', 'humidity', 'sunshine', 'elevation', 'soil_pH'];
const PARAM_LABEL = {
  rainfall:  'Rain',
  temp_max:  'T_max',
  temp_min:  'T_min',
  humidity:  'RH',
  sunshine:  'Sun',
  elevation: 'Elev',
  soil_pH:   'pH',
};
const scoreMarker = (v) => v === 1.0 ? '●' : v === 0.5 ? '◐' : '○';
const scoreClass  = (v) => v === 1.0 ? 'text-emerald-600'
                          : v === 0.5 ? 'text-amber-600'
                          : 'text-rose-500';
const barColor = (s) =>
    s >= 75 ? 'from-emerald-400 to-emerald-600'
  : s >= 50 ? 'from-lime-400 to-lime-600'
  : s >= 25 ? 'from-amber-400 to-amber-600'
  : 'from-rose-400 to-rose-600';

const Sp = ({ l, v, u }) => (
  <div className="rounded-md bg-icrisat-green-soft/40 border border-icrisat-green/15 p-2">
    <div className="text-[10px] uppercase text-icrisat-earth">{l}</div>
    <div className="font-display text-icrisat-green">
      {v ?? '—'} <span className="text-xs">{u}</span>
    </div>
  </div>
);

export const HiResPanel = ({ data }) => {
  const sp = data.site_params || {};
  const isLiveGee = (sp.elev_ph_source || '').startsWith('live_gee');
  const irr = data.irrigation_availability;
  const irrLabel = {
    full_season: 'Full-season irrigation',
    supplemental: 'Supplemental irrigation',
    rainfed: 'Rainfed',
  }[irr] || irr;
  const budget = data.budget_rs_per_ha;
  const labourDays = data.labour_budget_days_per_ha;
  const labourLabel = data.labour_availability;

  return (
    <div className="mt-5 space-y-5 animate-fade-up">
      {/* Site parameters card */}
      <div className="rounded-xl border border-icrisat-green/20 bg-white p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-display text-icrisat-green">Site parameters · {data.years_used}</div>
            {irr && (
              <div className="text-[11px] text-icrisat-earth mt-0.5">
                Ranked for <b>{irrLabel}</b>
                {budget != null && <> · Budget <b>₹{budget.toLocaleString('en-IN')}/ha</b></>}
                {labourLabel && <> · Labour <b>{labourLabel}</b> (~{labourDays} d/ha)</>}
              </div>
            )}
          </div>
          <Badge variant={isLiveGee ? 'default' : 'outline'}
                 className={isLiveGee ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-amber-50 border-amber-300 text-amber-800'}>
            {isLiveGee ? 'LIVE · GEE + Open-Meteo' : 'FALLBACK'}
          </Badge>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
          <Sp l="Rainfall"  v={sp.rainfall_mm_yr?.toFixed(0)} u="mm/yr" />
          <Sp l="T_max"     v={sp.temp_max_C?.toFixed(1)} u="°C" />
          <Sp l="T_min"     v={sp.temp_min_C?.toFixed(1)} u="°C" />
          <Sp l="Humidity"  v={sp.humidity_pct?.toFixed(0)} u="%" />
          <Sp l="Sunshine"  v={sp.sunshine_hrs?.toFixed(1)} u="hrs/day" />
          <Sp l="Elevation" v={sp.elevation_m?.toFixed(0)} u="m" />
          <Sp l="Soil pH"   v={sp.soil_pH?.toFixed(2)} u="" />
          <Sp l="Weather"   v={(sp.weather_source || '').split(' ')[0]} u={(sp.weather_source || '').split(' ')[2] || ''} />
        </div>
        {(sp.weather_warning || sp.gee_warning) && (
          <div className="mt-3 space-y-1 text-[11px] text-amber-800">
            {sp.weather_warning && <div>· {sp.weather_warning}</div>}
            {sp.gee_warning     && <div>· {sp.gee_warning}</div>}
          </div>
        )}
      </div>

      {/* Weight legend */}
      <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
        {Object.entries(data.weights).map(([k, v]) => (
          <div key={k} className="rounded-md bg-icrisat-green-soft/60 border border-icrisat-green/20 p-2 text-center">
            <div className="text-[10px] uppercase text-icrisat-earth">{PARAM_LABEL[k] || k}</div>
            <div className="font-display text-icrisat-green">{Math.round(v * 100)}%</div>
          </div>
        ))}
      </div>

      {/* Crop-wise suitability table */}
      <div className="rounded-xl border border-icrisat-green/20 bg-white overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-icrisat-green-soft/60">
              <TableHead className="w-12">#</TableHead>
              <TableHead>Crop</TableHead>
              <TableHead className="w-[160px]">Score</TableHead>
              <TableHead>Category</TableHead>
              {PARAM_ORDER.map(k => (
                <TableHead key={k} className="text-center">{PARAM_LABEL[k]}</TableHead>
              ))}
              <TableHead className="text-right hidden md:table-cell">Cost ₹/ha</TableHead>
              <TableHead className="text-right hidden md:table-cell">Labour (d)</TableHead>
              <TableHead className="text-center hidden lg:table-cell">OK / Marg / Fail</TableHead>
              <TableHead className="text-center hidden lg:table-cell">Irrigation ×</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.results.map((r, i) => (
              <TableRow key={r.crop}
                        data-testid={`hires-crop-row-${r.crop.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                        className={i === 0 ? 'bg-emerald-50/60' : ''}>
                <TableCell className="text-center text-icrisat-earth">{i + 1}</TableCell>
                <TableCell className="font-medium">{r.crop}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <span className="font-display text-icrisat-green tabular-nums w-10">{r.score}</span>
                    <div className="h-2 flex-1 rounded-full bg-slate-100 overflow-hidden min-w-[60px]">
                      <div className={`h-full rounded-full bg-gradient-to-r ${barColor(r.score)}`}
                           style={{ width: `${Math.min(100, r.score)}%` }} />
                    </div>
                  </div>
                  {r.score_raw != null && r.irrigation_multiplier !== 1.0 && (
                    <div className="text-[10px] text-slate-400 mt-0.5">raw {r.score_raw}</div>
                  )}
                </TableCell>
                <TableCell><Badge className={scoreColor(r.category)}>{r.category}</Badge></TableCell>
                {PARAM_ORDER.map(k => (
                  <TableCell key={k} className={`text-center ${scoreClass(r.param_scores[k])}`}>
                    {scoreMarker(r.param_scores[k])}
                  </TableCell>
                ))}
                <TableCell className="text-right hidden md:table-cell text-xs">
                  {r.input_cost_rs_per_ha != null ? (
                    <span className={r.cost_ok === false ? 'text-rose-600 font-medium' : ''}>
                      {r.input_cost_rs_per_ha.toLocaleString('en-IN')}
                      {r.cost_ok === false && ' ⚠'}
                    </span>
                  ) : '—'}
                </TableCell>
                <TableCell className="text-right hidden md:table-cell text-xs">
                  {r.labour_days_per_ha != null ? (
                    <span className={r.labour_ok === false ? 'text-rose-600 font-medium' : ''}>
                      {r.labour_days_per_ha}
                      {r.labour_ok === false && ' ⚠'}
                    </span>
                  ) : '—'}
                </TableCell>
                <TableCell className="text-center hidden lg:table-cell text-xs">
                  <span className="text-emerald-600">{r.params_ok}</span> /
                  <span className="text-amber-600"> {r.params_marginal}</span> /
                  <span className="text-rose-500"> {r.params_fail}</span>
                </TableCell>
                <TableCell className="text-center hidden lg:table-cell">
                  {r.irrigation_multiplier != null && r.irrigation_multiplier !== 1.0 ? (
                    <Badge variant="outline"
                           className={r.irrigation_multiplier > 1
                             ? 'bg-emerald-50 border-emerald-300 text-emerald-800'
                             : 'bg-amber-50 border-amber-300 text-amber-800'}>
                      ×{r.irrigation_multiplier}
                    </Badge>
                  ) : <span className="text-slate-400">×1.00</span>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="text-[11px] text-muted-foreground leading-relaxed">
        Legend: <span className="text-emerald-600">● in range</span> ·
        <span className="text-amber-600"> ◐ ±15% margin</span> ·
        <span className="text-rose-500"> ○ out of range</span>. Method: {data.method}.
      </div>

      {/* Top pick districts + soils footnote */}
      {data.top_pick && (
        <div className="text-xs text-muted-foreground bg-icrisat-green-soft/40 p-3 rounded-lg">
          <b>Top pick:</b> {data.top_pick.crop} · <b>Suited districts:</b> {data.top_pick.key_districts} ·
          <b> Recommended soils:</b> {data.top_pick.soil_types}
        </div>
      )}

      <AiAdvisor context="crop_selection_hires" payload={data} />
    </div>
  );
};

export default HiResPanel;
